import React, { useState, useEffect } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import {
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { useSearchCodesQuery } from 'src/common/apis/FlorenceApi';
import { ICD10Code } from 'src/common/types/Codes';
import IOSSwitch from 'src/florence/screens/TFactorScreen/IOSSwitch';

import { ReactComponent as SearchIcon } from '../../../common/assets/icons/search_icon.svg';

const useStyles = makeStyles({
  root: {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: '#266071',
      },
    },
  },
});

interface SearchICD10CodesProps {
  selectedCodes: ICD10Code[];
  updateICD10Code: (codes: ICD10Code) => void;
  suggestedICD10: ICD10Code[];
}

const SearchICD10Codes: React.FC<SearchICD10CodesProps> = ({
  selectedCodes,
  updateICD10Code,
  suggestedICD10,
}) => {
  const [value, setValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState(value);

  const classes = useStyles();

  // RTK Query hook for searching codes
  const { data: codes, isFetching } = useSearchCodesQuery(
    {
      text: debouncedValue,
      type: 'ICD10',
    },
    {
      skip: debouncedValue.length < 2,
    }
  );

  // Debounce input value
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [value]);

  const isSelected = (code: any) => {
    return !!selectedCodes.find((c) => c.code === code.code);
  };

  return (
    <Autocomplete
      style={{
        marginTop: '8px',
        marginBottom: '8px',
      }}
      freeSolo
      openOnFocus
      options={
        value.length
          ? codes ?? []
          : suggestedICD10.map((c) => {
              return {
                code: c.code,
                description: c.description,
              };
            })
      }
      getOptionLabel={(option) => value}
      inputValue={value}
      onInputChange={(event, newInputValue) => {
        setValue(newInputValue);
      }}
      disableCloseOnSelect
      loading={isFetching}
      loadingText='Searching...'
      noOptionsText='No ICD-10 Codes found'
      ListboxProps={{ style: { borderRadius: '16px' } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label=''
          placeholder={
            selectedCodes.length > 0
              ? 'Find additional ICD-10 codes'
              : 'Find ICD-10 codes'
          }
          variant='outlined'
          className={classes.root}
          InputProps={{
            ...params.InputProps,
            style: {
              color: 'rgba(38, 96, 113, 1)',
              borderRadius: '16px',
            },
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <React.Fragment>
                {isFetching ? (
                  <CircularProgress color='inherit' size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
          InputLabelProps={{
            ...params.InputLabelProps,
            style: { color: 'rgba(38, 96, 113, 0.8)' },
          }}
        />
      )}
      renderOption={(props, option) => {
        return (
          <li
            {...props}
            key={option.code}
            style={{
              padding: '12px',
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              cursor: 'auto',
            }}
          >
            <Box>
              <Typography color='darkBlue' fontWeight={700}>
                {option.code}
              </Typography>

              {option.description && (
                <Typography color='darkBlue' fontWeight={500} ml='4px'>
                  - {option.description}
                </Typography>
              )}
            </Box>
            <Box>
              <FormControlLabel
                style={{ marginRight: '0' }}
                control={
                  <IOSSwitch
                    sx={{ m: 1 }}
                    checked={isSelected(option)}
                    onChange={(event) =>
                      updateICD10Code({
                        ...option,
                        selected: event.target.checked,
                      })
                    }
                  />
                }
                label=''
              />
            </Box>
          </li>
        );
      }}
    />
  );
};

export default SearchICD10Codes;
